/**
 * DeviceMonitoringCard — authorize + toggle a device's parental-monitoring
 * collection (UPGRADE-03). `authorized` records that the parent's paper consent
 * was captured at provisioning and is the hard gate: un-authorizing forces both
 * channels off. Gated on `device.monitoring.manage`; read-only otherwise.
 * Collection itself (the SMS/call data) is never shown here — that's a separate,
 * audited reveal.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Device } from "@dfp/api-client";
import { Button } from "./Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "./Card.js";
import { FormError, apiErrorMessage } from "./Form.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";

export function DeviceMonitoringCard({ device }: { readonly device: Device }): JSX.Element {
  const auth = useAuth();
  const canManage = (auth.user?.permissions ?? []).includes("device.monitoring.manage");
  const qc = useQueryClient();

  const [authorized, setAuthorized] = useState(device.monitoringAuthorized);
  const [sms, setSms] = useState(device.smsMonitoringEnabled);
  const [calls, setCalls] = useState(device.callsMonitoringEnabled);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.post<Device>(`/api/devices/${device.id}/monitoring`, {
        authorized,
        smsEnabled: authorized && sms,
        callsEnabled: authorized && calls,
      }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["devices", "byId", device.id] });
    },
    onError: (e: unknown) => setError(apiErrorMessage(e)),
  });

  const dirty =
    authorized !== device.monitoringAuthorized ||
    sms !== device.smsMonitoringEnabled ||
    calls !== device.callsMonitoringEnabled;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Parental monitoring</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3 text-sm">
        <p className="text-simuflex-slate">
          Collects SMS + call logs to the backend for the (separately built) parent app. Only with
          the parent&apos;s signed consent captured at provisioning.
        </p>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={authorized}
            disabled={!canManage || save.isPending}
            onChange={(e) => {
              const v = e.target.checked;
              setAuthorized(v);
              if (!v) {
                setSms(false);
                setCalls(false);
              }
            }}
          />
          <span className="font-medium text-simuflex-navy">
            Authorized (parent consent on file)
          </span>
        </label>
        {device.monitoringAuthorizedAt ? (
          <p className="text-xs text-simuflex-slate">
            Authorized since {device.monitoringAuthorizedAt.slice(0, 10)}
          </p>
        ) : null}

        <div className="ml-6 space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={sms}
              disabled={!canManage || !authorized || save.isPending}
              onChange={(e) => setSms(e.target.checked)}
            />
            <span>Collect SMS (message bodies)</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={calls}
              disabled={!canManage || !authorized || save.isPending}
              onChange={(e) => setCalls(e.target.checked)}
            />
            <span>Collect call logs</span>
          </label>
        </div>

        <FormError message={error} />
        {canManage ? (
          <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        ) : (
          <p className="text-xs text-simuflex-slate">
            Read-only — requires <code>device.monitoring.manage</code>.
          </p>
        )}
      </CardBody>
    </Card>
  );
}
